import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useRef } from "react";

interface SliderProps {
  images: string[];
  width?: string;
  height?: string;
  duration?: number;
  thumbnail?: boolean;
  animation?: "slide" | "fade" | "infinite" | "none";
  arrow?: boolean;
  fadeDurationMs?: number;
}

export const Slider: React.FC<SliderProps> = ({
  images = [],
  width = "full",
  height = "500px",
  duration = null,
  thumbnail,
  animation = "none",
  arrow = true,
  fadeDurationMs = 800
}) => {
  const [current, setCurrent] = useState(0);
  // Stocke l'index précédent pour permettre un vrai cross-fade
  const [previous, setPrevious] = useState<number | null>(null);
  // Active l'animation de slide (transform) pour le mode slide infini
  const [slideActive, setSlideActive] = useState(false);
  // État pour contrôler l'opacité de l'image courante en mode fade
  const [fadeOpacity, setFadeOpacity] = useState(1);
  const fadeTimeoutRef = useRef<number | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  if (animation === "infinite") {
    arrow = false;
    thumbnail = false;
  }

  // Préchargement de toutes les images pour éviter les requêtes réseau à chaque changement
  useEffect(() => {
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [images]);

  useEffect(() => {
    if (!duration) return;

    const interval = setInterval(() => {
      // Utiliser la même logique de navigation pour garder previous
      const next = (current + 1) % images.length;
      goToImage(next);
    }, duration);

    return () => clearInterval(interval);
  }, [duration, images.length, current]);

  const goToImage = (index: number) => {
    if (animation === "infinite") {
      const n = images.length;
      if (n === 0) return;
      const normalized = ((index % n) + n) % n;
      setPrevious(current);
      setCurrent(normalized);
      // Préparer la frame initiale (pas de transition)
      setSlideActive(false);
      // Lancer la transition sur la frame suivante (double rAF pour fiabilité)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSlideActive(true);
        });
      });
      // Nettoyer l'image précédente après l'animation d'entrée
      if (fadeTimeoutRef.current) {
        window.clearTimeout(fadeTimeoutRef.current);
      }
      fadeTimeoutRef.current = window.setTimeout(() => {
        setPrevious(null);
        setSlideActive(false);
      }, Math.max(0, fadeDurationMs + 50));
      return;
    }
    if (animation === "fade") {
      setPrevious(current);
      // Réinitialiser l'opacité à 0 pour la nouvelle image
      setFadeOpacity(0);
      // Nettoyer l'image précédente après la durée de transition
      if (fadeTimeoutRef.current) {
        window.clearTimeout(fadeTimeoutRef.current);
      }
      // Faire apparaître la nouvelle image après un court délai
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setFadeOpacity(1);
        });
      });
      fadeTimeoutRef.current = window.setTimeout(() => {
        setPrevious(null);
      }, Math.max(0, fadeDurationMs + 50)); // légèrement > pour garantir la fin de transition
    }
    setCurrent(index);
  };

  // Nettoyage du timeout au démontage
  useEffect(() => {
    return () => {
      if (fadeTimeoutRef.current) {
        window.clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, []);

  // Gestion du swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (
      touchStartX.current !== null &&
      touchEndX.current !== null
    ) {
      const delta = touchStartX.current - touchEndX.current;
      //40 c'est le seuil pour changer d'image
      if (delta > 40 && (animation === "infinite" || current < images.length - 1)) {
        // Swipe gauche (image suivante)
        goToImage(animation === "infinite" ? (current + 1) % images.length : current + 1);
      } else if (delta < -40 && (animation === "infinite" || current > 0)) {
        // Swipe droite (image précédente)
        goToImage(animation === "infinite" ? (current - 1 + images.length) % images.length : current - 1);
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div
      className={clsx(
        "flex flex-col items-center",
        width === "full" && "-mx-body w-full-nomargin"
      )}
      style={{
        width: width === "full" ? undefined : width
      }}
    >
      <div
        className={clsx(
          "relative w-full overflow-hidden shadow-md",
          width !== "full" && "rounded-2xl"
        )}
        style={{ height }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >

        {/* Flèches gauche/droite */}
        {current > 0 && arrow && (
          <button
            aria-label="Image de gauche"
            onClick={() => goToImage(current - 1)}
            className="absolute top-1/2 left-4 -translate-y-1/2 bg-white/70 hover:bg-white text-black p-2 rounded-full shadow-md z-20"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
        )}
        {current < images.length - 1 && arrow && (
          <button
            aria-label="Image de droite"
            onClick={() => goToImage(current + 1)}
            className="absolute top-1/2 right-4 -translate-y-1/2 bg-white/70 hover:bg-white text-black p-2 rounded-full shadow-md z-20"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        )}

        {/* Images en slide */}
        {animation === "slide" ? (
          <div
            className="flex h-full transition-transform duration-500"
            style={{
              width: `${images.length * 100}%`,
              transform: `translateX(-${(100 / images.length) * current}%)`
            }}
          >
            {images.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={`Slide ${idx}`}
                className="w-full h-full object-cover flex-shrink-0"
                style={{ width: `${100 / images.length}%` }}
              />
            ))}
          </div>
        ) : animation === "infinite" ? (
          // Superposition avec poussée: previous glisse vers la gauche, current arrive de la droite
          <div className="absolute inset-0 pointer-events-none">
            {previous !== null && (
              <img
                src={images[previous]}
                alt={`Slide ${previous}`}
                className="absolute inset-0 w-full h-full object-cover z-0"
                style={{
                  transform: slideActive ? "translateX(-100%)" : "translateX(0%)",
                  transitionProperty: "transform",
                  transitionDuration: `${fadeDurationMs}ms`,
                  transitionTimingFunction: "ease",
                  willChange: "transform"
                }}
              />
            )}
            <img
              src={images[current]}
              alt={`Slide ${current}`}
              className="absolute inset-0 w-full h-full object-cover z-10"
              style={{
                transform: previous === null ? "translateX(0%)" : (slideActive ? "translateX(0%)" : "translateX(100%)"),
                transitionProperty: "transform",
                transitionDuration: `${fadeDurationMs}ms`,
                transitionTimingFunction: "ease",
                willChange: "transform"
              }}
              key={current}
            />
          </div>
        ) : (
          // Fade ou none
          <>
            {animation === "fade" ? (
              <div className="absolute inset-0 pointer-events-none">
                {/* Toutes les images restent dans le DOM pour éviter les requêtes réseau */}
                {images.map((img, idx) => {
                  const isCurrent = idx === current;
                  const isPrevious = idx === previous;
                  
                  return (
                    <img
                      key={idx}
                      src={img}
                      alt={`Slide ${idx}`}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ 
                        opacity: isCurrent ? fadeOpacity : (isPrevious ? 1 : 0),
                        zIndex: isCurrent ? 10 : (isPrevious ? 0 : -1),
                        transition: `opacity ${fadeDurationMs}ms ease-in-out`,
                        willChange: "opacity",
                        pointerEvents: "none"
                      }}
                    />
                  );
                })}
              </div>
            ) : (
              <img
                src={images[current]}
                alt={`Slide ${current}`}
                className="w-full h-full object-cover"
                key={current}
              />
            )}
          </>
        )}
      </div>

      {/* Miniatures*/}
      {thumbnail && (
        <div className="flex mt-4 gap-2 overflow-x-auto max-w-full pb-1">
          {images.map((img, index) => (
            <button
              key={index}
              onClick={() => goToImage(index)}
              className={clsx(
                'h-16 aspect-video rounded-md overflow-hidden border-2 transition',
                index === current ? "border-blue-500" : "border-transparent"
              )}
              style={{ minWidth: 80, maxWidth: 120 }}
            >
              <img
                src={img}
                alt={`Miniature ${index}`}
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

    </div>
  );
};